import { capitalize } from 'lodash';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CodeSample } from '~/eds/src/components/CodeSample/CodeSample';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { copy } from '~/eds/src/utils/copy';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

import { EndpointConfiguration } from '../Configuration/EndpointConfiguration';
import {
  ConfigurationType,
  Endpoint,
  EndpointType,
  SamplePayloadsType,
} from '../types';
import {
  getDisabledCopyTooltip,
  getDisabledTooltip,
  getEndpointTitle,
  getEventSamplePayload,
} from '../utils';

interface Props {
  action: Nullable<EndpointType>;
  endpoint?: Nullable<Endpoint>;
  sampleEvent?: Nullable<string>;
  configuration?: Nullable<ConfigurationType>;
  isLoading?: boolean;
  samplePayloads?: SamplePayloadsType;
}

export const EndpointModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [createEndpoint, { isLoading: isCreatingEndpoint }] =
    api.endpoints.createEndpoint.useMutation();

  const [deleteEndpoint, { isLoading: isDeletingEndpoint }] =
    api.endpoints.deleteEndpoint.useMutation();

  const [suspendEndpoint, { isLoading: isSuspendingEndpoint }] =
    api.endpoints.suspendEndpoint.useMutation();

  const [activateEndpoint, { isLoading: isActivatingEndpoint }] =
    api.endpoints.activateEndpoint.useMutation();

  const [updateEndpoint, { isLoading: isUpdatingEndpoint }] =
    api.endpoints.updateEndpoint.useMutation();

  const [rotateEndpointKey, { isLoading: isRotatingEndpointKey }] =
    api.endpoints.rotateEndpointKey.useMutation();

  const {
    activeEndpoint,
    activeEndpointType: action,
    sampleEvent,
    configuration,
  } = useSelector(slices.webhooks.selectors.selectEndpointConfiguration);

  const { data: eventSamplePayload, isLoading: isLoadingEventSamplePayload } =
    api.endpoints.getEventSamplePayload.useQuery(undefined);

  if (action === 'view' || action === null) {
    return null;
  }

  const handleHide = () =>
    dispatch(slices.webhooks.actions.resetEndpointConfiguration());

  const handleActionConfirm = () => {
    const queries = {
      delete: deleteEndpoint,
      suspend: suspendEndpoint,
      edit: updateEndpoint,
      add: createEndpoint,
      activate: activateEndpoint,
      'rotate-key': rotateEndpointKey,
    };

    const query = queries[action];

    query({
      configuration,
      endpoint: activeEndpoint!,
    })
      ?.unwrap()
      .then(() => {
        toast({
          message: `${toastMessage?.success}`,
          status: 'success',
        });
      })
      .catch(() =>
        toast({
          message: `${toastMessage?.failure}`,
          status: 'danger',
        }),
      )
      .finally(handleHide);
  };

  const isLoading =
    isDeletingEndpoint ||
    isSuspendingEndpoint ||
    isUpdatingEndpoint ||
    isCreatingEndpoint ||
    isActivatingEndpoint ||
    isRotatingEndpointKey;

  const {
    children,
    isFullPage,
    variant,
    panel,
    toastMessage,
    tooltip,
    disabled,
  } =
    resolveModalProps({
      action,
      endpoint: activeEndpoint,
      sampleEvent,
      samplePayloads: eventSamplePayload,
      isLoading: isLoadingEventSamplePayload,
      configuration,
      intl,
    }) ?? {};

  const modalProps = {
    children,
    isFullPage,
    isVisible: Boolean(action),
    panel,
    primaryAction: {
      disabled,
      isLoading,
      onClick: handleActionConfirm,
      tooltip,
      text: `${capitalize(action ?? '')}`,
      variant,
    },
    onCancel: handleHide,
    onHide: handleHide,
    title: getEndpointTitle(action, intl),
  };

  return <Modal {...modalProps} />;
};

const resolveModalProps = ({
  action,
  endpoint,
  sampleEvent,
  samplePayloads,
  isLoading,
  configuration,
  intl,
}: Props & { intl: any }) => {
  const endpointUrl = endpoint?.endpointUrl ?? '';
  const eventSamplePayload = getEventSamplePayload(sampleEvent, samplePayloads);
  const disabledTooltip = getDisabledTooltip(configuration ?? null, intl);
  const disabledCopyTooltip = getDisabledCopyTooltip({
    sampleEvent,
    samplePayloads,
    intl,
  });

  switch (action) {
    case 'delete': {
      return {
        children: (
          <Text>
            <FormattedMessage
              id="EndpointModal.deleteConfirmation"
              defaultMessage="Once this endpoint is deleted, no more events will be sent. The previous attempts will be archived. Are you sure you want to delete {endpointUrl}?"
              description="Confirmation message for deleting endpoint. Example: endpointUrl = https://api.example.com/webhook"
              values={{ endpointUrl }}
            />
          </Text>
        ),
        variant: 'danger' as const,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'EndpointModal.deleteSuccess',
              defaultMessage: 'Endpoint {endpointUrl} deleted.',
              description:
                'Success message for endpoint deletion. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
          failure: intl.formatMessage(
            {
              id: 'EndpointModal.deleteFailure',
              defaultMessage:
                'There was an error deleting {endpointUrl}. Please try again.',
              description:
                'Error message for endpoint deletion. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
        },
        isFullPage: false,
      };
    }
    case 'suspend': {
      return {
        children: (
          <Text>
            <FormattedMessage
              id="EndpointModal.suspendConfirmation"
              defaultMessage="Evisort will not send any events until the endpoint is activated again. Are you sure you want to suspend {endpointUrl}?"
              description="Confirmation message for suspending endpoint. Example: endpointUrl = https://api.example.com/webhook"
              values={{ endpointUrl }}
            />
          </Text>
        ),
        variant: 'danger' as const,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'EndpointModal.suspendSuccess',
              defaultMessage: 'Endpoint `{endpointUrl}` suspended.',
              description:
                'Success message for endpoint suspension. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
          failure: intl.formatMessage(
            {
              id: 'EndpointModal.suspendFailure',
              defaultMessage:
                'There was an error suspending {endpointUrl}. Please try again.',
              description:
                'Error message for endpoint suspension. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
        },
        isFullPage: false,
      };
    }
    case 'rotate-key': {
      return {
        children: (
          <Text>
            <FormattedMessage
              id="EndpointModal.rotateKeyConfirmation"
              defaultMessage="Evisort will send new events immediately with the new secret key. The old key will be available for 72 hours. Are you sure you want to rotate the key for {endpointUrl}?"
              description="Confirmation message for rotating endpoint key. Example: endpointUrl = https://api.example.com/webhook"
              values={{ endpointUrl }}
            />
          </Text>
        ),
        variant: 'primary' as const,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'EndpointModal.rotateKeySuccess',
              defaultMessage: 'Key for endpoint {endpointUrl} rotated.',
              description:
                'Success message for key rotation. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
          failure: intl.formatMessage(
            {
              id: 'EndpointModal.rotateKeyFailure',
              defaultMessage:
                'There was an error rotating the key for {endpointUrl}. Please try again.',
              description:
                'Error message for key rotation. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
        },
        isFullPage: false,
      };
    }
    case 'activate': {
      return {
        children: (
          <Text>
            <FormattedMessage
              id="EndpointModal.activateConfirmation"
              defaultMessage="Are you sure you want to re-activate {endpointUrl}?"
              description="Confirmation message for activating endpoint. Example: endpointUrl = https://api.example.com/webhook"
              values={{
                endpointUrl,
              }}
            />
          </Text>
        ),
        variant: 'primary' as const,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'EndpointModal.activateSuccess',
              defaultMessage: 'Endpoint {endpointUrl} active.',
              description:
                'Success message for endpoint activation. Example: endpointUrl = ',
            },
            { endpointUrl },
          ),
          failure: intl.formatMessage(
            {
              id: 'EndpointModal.activateFailure',
              defaultMessage:
                'There was an error re-activating {endpointUrl}. Please try again.',
              description:
                'Error message for endpoint activation. Example: endpointUrl = "https://example.com"',
            },
            { endpointUrl },
          ),
        },
        isFullPage: false,
      };
    }
    case 'edit':
    case 'add':
      return {
        children: <EndpointConfiguration />,
        disabled: Boolean(disabledTooltip),
        tooltip: disabledTooltip,
        panel: {
          actions: [
            {
              disabled: !sampleEvent || eventSamplePayload === null,
              tooltip: disabledCopyTooltip,
              text: intl.formatMessage({
                id: 'EndpointModal.copyAction',
                defaultMessage: 'Copy',
                description: 'Copy button text',
              }),
              icon: 'clipboard' as const,
              onClick: () => {
                copy(eventSamplePayload!);
              },
            },
          ],
          children: <CodeSample sample={eventSamplePayload} />,
          loadingContent: {
            isLoading,
            message: intl.formatMessage({
              id: 'EndpointModal.loadingMessage',
              defaultMessage: 'Loading event sample payload…',
              description: 'Loading message for sample payload',
            }),
          },
          placeholderContent: !sampleEvent
            ? {
                image: 'empty-no-groups',
                message: intl.formatMessage({
                  id: 'EndpointModal.previewMessage',
                  defaultMessage: 'Preview code when clicking on events…',
                  description: 'Message when no event is selected',
                }),
              }
            : !eventSamplePayload
              ? {
                  image: 'empty-no-groups',
                  message: intl.formatMessage({
                    id: 'EndpointModal.noSamplePayloadMessage',
                    defaultMessage:
                      'This event type does not have a sample payload. Please try another event type.',
                    description: 'Message when event has no sample payload',
                  }),
                }
              : undefined,
          title: intl.formatMessage({
            id: 'EndpointModal.samplePayloadTitle',
            defaultMessage: 'Sample Payload',
            description: 'Title for sample payload panel',
          }),
          width: 'l',
        },
        isFullPage: true,
        status: 'primary' as const,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'EndpointModal.addSuccess',
              defaultMessage: 'Endpoint `{url}` added.',
              description:
                'Success message for endpoint creation. Example: url = ',
            },
            { url: configuration?.url },
          ),
          failure: intl.formatMessage({
            id: 'EndpointModal.addFailure',
            defaultMessage:
              'There was an error creating the endpoint. Please try again.',
            description: 'Error message for endpoint creation',
          }),
        },
        variant: 'primary' as const,
      };
    case 'view':
    default:
      return null;
  }
};
